import { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import * as Dialog from '@radix-ui/react-dialog';
import { Command } from 'cmdk';
import { Search, CornerDownLeft, ArrowUp, ArrowDown } from 'lucide-react';
import {
  PINNED_TOP,
  PINNED_REPORTS,
  PINNED_SYSTEM,
  visibleWorkspaces,
  visibleTabs,
  matchesPermission,
} from '@/config/navigation';
import { useAuthStore } from '@/stores/auth.store';

/**
 * ⌘K / Ctrl+K command palette (parity with hms_light). Fuzzy-filters every
 * permission-visible nav destination — pinned quick links + each workspace's
 * tabs. Pure client-side over the nav manifest, no API calls. Filtering and
 * keyboard nav (↑/↓/↵) are handled by `cmdk`.
 */
interface Dest {
  to: string;
  label: string;
  group: string;
  icon: React.ElementType;
}

export function CommandPalette({
  open,
  onOpenChange,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const { t } = useTranslation('nav');
  const navigate = useNavigate();
  const hasPermission = useAuthStore((s) => s.hasPermission);
  const isSuperAdmin = useAuthStore((s) => s.user?.isSuperAdmin ?? false);
  const permissionsKey = useAuthStore((s) => s.user?.permissions);

  const groups = useMemo<{ heading: string; items: Dest[] }[]>(() => {
    const result: { heading: string; items: Dest[] }[] = [];

    // Pinned quick links (Home, POS, Reports, Store Guide, Platform…)
    const quickHeading = t('quickLinks', { defaultValue: 'Quick links' });
    const pinnedItems = [...PINNED_TOP, PINNED_REPORTS, ...PINNED_SYSTEM]
      .filter((p) => matchesPermission(p.permission, hasPermission) && (!p.superAdminOnly || isSuperAdmin))
      .map((p) => ({ to: p.href, label: t(p.labelKey), group: quickHeading, icon: p.icon }));
    if (pinnedItems.length) result.push({ heading: quickHeading, items: pinnedItems });

    // Every visible workspace tab
    for (const ws of visibleWorkspaces(hasPermission)) {
      const wsLabel = t(ws.labelKey);
      const items = visibleTabs(ws, hasPermission).map((tab) => ({
        to: `${ws.basePath}/${tab.segment}`,
        label: t(tab.labelKey),
        group: wsLabel,
        icon: tab.icon ?? ws.icon,
      }));
      if (items.length) result.push({ heading: wsLabel, items });
    }
    return result;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [t, hasPermission, isSuperAdmin, permissionsKey]);

  const select = (to: string) => {
    onOpenChange(false);
    navigate(to);
  };

  return (
    <Dialog.Root open={open} onOpenChange={onOpenChange}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 z-[60] bg-scrim/40 backdrop-blur-sm data-[state=open]:animate-in data-[state=open]:fade-in" />
        <div className="fixed inset-0 z-[60] flex items-start justify-center overflow-y-auto p-4 pt-[12vh]">
          <Dialog.Content aria-label={t('paletteAria', { defaultValue: 'Command palette' })} className="w-full max-w-xl focus:outline-none">
            <Dialog.Title className="sr-only">{t('paletteAria', { defaultValue: 'Command palette' })}</Dialog.Title>
            <Dialog.Description className="sr-only">{t('palettePlaceholder', { defaultValue: 'Search pages and actions' })}</Dialog.Description>
            <Command
              loop
              className="glass-panel animate-scale-in overflow-hidden rounded-xl shadow-2xl"
              filter={(value, search) => (value.toLowerCase().includes(search.toLowerCase()) ? 1 : 0)}
            >
              <div className="flex items-center gap-2.5 border-b border-border/60 px-4">
                <Search className="h-4 w-4 shrink-0 text-muted-foreground" />
                <Command.Input
                  autoFocus
                  placeholder={t('palettePlaceholder', { defaultValue: 'Search pages and actions…' })}
                  className="h-12 w-full bg-transparent text-sm placeholder:text-muted-foreground/60 focus:outline-none"
                />
              </div>

              <Command.List className="max-h-[52vh] overflow-y-auto p-2">
                <Command.Empty className="px-3 py-8 text-center text-sm text-muted-foreground">
                  {t('paletteNoMatch', { defaultValue: 'No matching pages.' })}
                </Command.Empty>
                {groups.map((g) => (
                  <Command.Group
                    key={g.heading}
                    heading={g.heading}
                    className="mb-1 [&_[cmdk-group-heading]]:px-3 [&_[cmdk-group-heading]]:pb-1 [&_[cmdk-group-heading]]:pt-2 [&_[cmdk-group-heading]]:text-[11px] [&_[cmdk-group-heading]]:font-semibold [&_[cmdk-group-heading]]:uppercase [&_[cmdk-group-heading]]:tracking-wider [&_[cmdk-group-heading]]:text-muted-foreground/60"
                  >
                    {g.items.map((item) => (
                      <Command.Item
                        key={`${g.heading}:${item.to}`}
                        value={`${item.label} ${item.group} ${item.to}`}
                        onSelect={() => select(item.to)}
                        className="flex cursor-pointer items-center gap-3 rounded-lg px-3 py-2 text-sm text-muted-foreground transition-colors aria-selected:bg-primary/10 aria-selected:text-foreground"
                      >
                        <item.icon className="h-4 w-4 shrink-0" />
                        <span className="truncate font-medium text-foreground">{item.label}</span>
                        <span className="ms-auto truncate text-xs text-muted-foreground/70">{item.group}</span>
                      </Command.Item>
                    ))}
                  </Command.Group>
                ))}
              </Command.List>

              <div className="flex items-center gap-4 border-t border-border/60 px-4 py-2 text-[11px] text-muted-foreground/70">
                <span className="flex items-center gap-1">
                  <CornerDownLeft className="h-3 w-3" /> {t('paletteOpenHint', { defaultValue: 'Open' })}
                </span>
                <span className="flex items-center gap-1">
                  <ArrowUp className="h-3 w-3" />
                  <ArrowDown className="h-3 w-3" /> {t('paletteNavHint', { defaultValue: 'Navigate' })}
                </span>
                <span className="ms-auto">esc</span>
              </div>
            </Command>
          </Dialog.Content>
        </div>
      </Dialog.Portal>
    </Dialog.Root>
  );
}
